import type { RefObject } from 'react';
import { useGridSettingsSafe } from '../store/selectors/useGridSettingsSafe';
import { useEagGridStore } from '../store/useEagGridStore';
import type { EagColumn } from '../types/EagColumn';
import { measureColumnContentWidth } from '../utils/autosizeColumn';
import { getHeaderButtonWidth } from '../utils/getHeaderButtonWidth';
type UseColumnAutosizeReturn = {
  autosizeColumn: (key: string) => void;
  autosizeAllColumns: () => void;
};

/**
 * Provides column auto-sizing callbacks that dynamically measure content width.
 *
 * Reads `headerHeight` from the grid settings store and accepts `showColumnMenu`
 * as a parameter to compute the exact header button region width — no hardcoded
 * padding constants.
 *
 * The measurement clones each cell into an offscreen container to read the
 * unconstrained `offsetWidth`, which already includes all CSS padding and
 * renderer-internal spacing (chips, flags, icons, etc.).
 *
 * @param tableRef - Ref to the rendered `<table>` element
 * @param flatColumnMap - Stable Map<key, EagColumn> for looking up per-column minWidth
 * @param showColumnMenu - Whether the column menu (sort + menu buttons) is visible in header cells
 */
export const useColumnAutosize = (tableRef: RefObject<HTMLTableElement | null>, flatColumnMap: Map<string, EagColumn>, showColumnMenu: boolean): UseColumnAutosizeReturn => {
  const store = useEagGridStore();
  const gridSettings = useGridSettingsSafe();
  const headerButtonWidth = getHeaderButtonWidth(gridSettings.headerHeight, showColumnMenu);
  const autosizeColumn = (key: string) => {
    if (!tableRef.current) {
      return;
    }
    const col = flatColumnMap.get(key);
    const width = measureColumnContentWidth(tableRef.current, key, {
      minWidth: col?.minWidth,
      headerButtonWidth
    });
    if (width != null) {
      store.setColumnWidth(key, width);
    }
  };
  const autosizeAllColumns = () => {
    if (!tableRef.current) {
      return;
    }
    const table = tableRef.current;
    const updates = store.getState().columnStates.filter(cs => cs.hidden !== true).reduce<Record<string, number>>((acc, cs) => {
      const col = flatColumnMap.get(cs.key);
      const width = measureColumnContentWidth(table, cs.key, {
        minWidth: col?.minWidth,
        headerButtonWidth
      });
      if (width != null) {
        acc[cs.key] = width;
      }
      return acc;
    }, {});
    store.setColumnStates(store.getState().columnStates.map(cs => cs.key in updates ? {
      ...cs,
      width: updates[cs.key]
    } : cs));
  };
  return {
    autosizeColumn,
    autosizeAllColumns
  };
};